// Список договоров: богатый сайдбар + фильтры type/status из URL + создание.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, FileDown } from 'lucide-react';
import { contractsApi, counterpartiesApi, exportApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { RichSidebar } from '@/components/layout/RichSidebar';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatMoney, formatDate, contractStatusColor } from '@/lib/format';

const TYPE_LABEL: Record<string, string> = {
  customer: 'Договоры (заключение)', subcontract: 'Договоры с подрядчиками', supply: 'Договоры с поставщиками',
};
const STATUS_LABEL: Record<string, string> = {
  signing: 'На подписи', warranty: 'Гарантийные обязательства', in_work: 'В работе',
};

export function ContractsListPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);

  const type = params.get('type') || undefined;
  const status = params.get('status') || undefined;
  const filters = { ...(type ? { type } : {}), ...(status ? { status } : {}) };

  const { data, isLoading } = useQuery({
    queryKey: ['contracts', filters],
    queryFn: () => contractsApi.list(filters),
  });

  const heading = type ? TYPE_LABEL[type] : status ? STATUS_LABEL[status] : 'Все договоры';

  return (
    <AppLayout sidebar={<RichSidebar />}>
      <PageHeader title={heading} subtitle={data ? `${data.count} договоров` : undefined}
        actions={<div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => exportApi.contracts()}><FileDown size={16} /> Excel</Button>
          {can('contracts', 'add') && (
            <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> Новый договор</Button>
          )}
        </div>} />
      <div className="panel">
        {isLoading ? <Spinner label="Загрузка…" /> :
          !data || data.results.length === 0 ? (
            <EmptyState title="Договоров нет" hint="В этом разделе пока пусто" />
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Номер</th><th>Тип</th><th>Контрагент</th><th>Объект</th><th>Статус</th><th style={{ textAlign: 'right' }}>Сумма</th><th>Срок</th></tr>
              </thead>
              <tbody>
                {data.results.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/contracts/${c.id}`)}>
                    <td className="mono">{c.number}</td>
                    <td>{c.type_display}</td>
                    <td>{c.counterparty_name}</td>
                    <td>{c.object_address || '—'}</td>
                    <td><StatusBadge label={c.status_display} color={contractStatusColor(c.status)} /></td>
                    <td style={{ textAlign: 'right' }} className="mono">{formatMoney(c.total_amount)}</td>
                    <td className="mono">{formatDate(c.date_end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      <CreateContractModal open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={(id) => { qc.invalidateQueries({ queryKey: ['contracts'] }); qc.invalidateQueries({ queryKey: ['nav-counts'] }); toast('Договор создан', 'success'); navigate(`/contracts/${id}`); }}
        onError={(msg) => toast(msg, 'error')} />
    </AppLayout>
  );
}

function CreateContractModal({ open, onClose, onCreated, onError }: {
  open: boolean; onClose: () => void; onCreated: (id: number) => void; onError: (msg: string) => void;
}) {
  const [number, setNumber] = useState('');
  const [type, setType] = useState('customer');
  const [counterparty, setCounterparty] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const { data: cps } = useQuery({ queryKey: ['counterparties'], queryFn: () => counterpartiesApi.list(), enabled: open });
  const mutation = useMutation({
    mutationFn: () => contractsApi.create({
      number, type: type as 'customer' | 'subcontract' | 'supply',
      counterparty: Number(counterparty), total_amount: amount || '0',
    }),
    onSuccess: (c) => { onClose(); onCreated(c.id); },
    onError: (e) => onError(getErrorMessage(e)),
  });
  return (
    <Modal open={open} onClose={onClose} title="Новый договор"
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!number || !counterparty || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Создание…' : 'Создать'}
        </Button>
      </>}>
      <div className="field">
        <label className="field__label">Номер договора</label>
        <input className="field__input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="ДГ-2025-001" />
      </div>
      <div className="field">
        <label className="field__label">Тип</label>
        <select className="field__select" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="customer">С заказчиком (ФКР)</option>
          <option value="subcontract">Субподряд</option>
          <option value="supply">Поставка</option>
        </select>
      </div>
      <div className="field">
        <label className="field__label">Контрагент</label>
        <select className="field__select" value={counterparty} onChange={(e) => setCounterparty(e.target.value ? Number(e.target.value) : '')}>
          <option value="">— выберите —</option>
          {cps?.results.map((cp) => <option key={cp.id} value={cp.id}>{cp.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="field__label">Сумма договора, ₽</label>
        <input className="field__input" value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} placeholder="2500000" />
      </div>
    </Modal>
  );
}
